import { Map as ImmutableMap } from 'immutable';

import { validateWorkExperiences } from 'shared/components/WorkExperienceControl';
import { parsePhoneNumber } from 'shared/helpers/parsers';
import { validateEmail, validateRequired } from 'shared/helpers/validations';

import parseFullName from './parseFullName';

export type TalentProfileFieldName =
  | 'currentLocation'
  | 'desiredLocations'
  | 'educationLevel'
  | 'email'
  | 'fullName'
  | 'jobSearchStatus'
  | 'nurseSpecialties'
  | 'password'
  | 'phone'
  | 'resumeFormData'
  | 'school'
  | 'shifts'
  | 'startDateOption'
  | 'stateLicenses'
  | 'whyChange'
  | 'workExperienceMethod'
  | 'workExperiences'
  | 'workPersonality';

export type NurseSpecialtyDescriptionId = number;

export type WorkExperienceIndex = number;

export type WorkExperienceFieldName =
  | 'companyName'
  | 'endDate'
  | 'jobTitle'
  | 'location'
  | 'startDate';

export type NurseSpecialtiesErrors = ImmutableMap<
  NurseSpecialtyDescriptionId,
  string
>;

export type WorkExperienceFieldErrors = ImmutableMap<
  WorkExperienceFieldName,
  string
>;

export type WorkExperiencesErrors = ImmutableMap<
  WorkExperienceIndex | 'base',
  WorkExperienceFieldErrors | string
>;

export type TalentProfileFieldError<K extends TalentProfileFieldName> =
  K extends 'nurseSpecialties'
    ? NurseSpecialtiesErrors
    : K extends 'workExperiences'
      ? WorkExperiencesErrors
      : string;

export type TalentProfileErrorValue =
  | string
  | NurseSpecialtiesErrors
  | WorkExperiencesErrors;

export type TalentProfileValidationErrors = ImmutableMap<
  string,
  TalentProfileErrorValue
>;

export function getFieldError<K extends TalentProfileFieldName>(
  errors: TalentProfileValidationErrors,
  key: K
): TalentProfileFieldError<K> | undefined {
  return errors.get(key) as TalentProfileFieldError<K> | undefined;
}

export function flattenValidationErrors(
  errors: TalentProfileValidationErrors
): ImmutableMap<string, string> {
  return errors
    .map((err): string => {
      if (typeof err === 'string') return err;
      const topLevelString = err
        .valueSeq()
        .find((value) => typeof value === 'string');
      if (typeof topLevelString === 'string') return topLevelString;
      const first = err.first();
      if (first === undefined) return '';
      if (typeof first === 'string') return first;
      return first.first() ?? '';
    })
    .toMap();
}

const defaultAllRequiredKeys: readonly TalentProfileFieldName[] = [
  'currentLocation',
  'desiredLocations',
  'email',
  'educationLevel',
  'fullName',
  'jobSearchStatus',
  'password',
  'phone',
  'shifts',
  'startDateOption',
  'workExperienceMethod',
  'workPersonality',
  'whyChange',
];

function additionalErrorForKey(
  talentProfile: any,
  key: string
): TalentProfileErrorValue | null {
  let error: TalentProfileErrorValue | null = null;

  switch (key) {
    case 'desiredLocations': {
      const completeLocations = talentProfile.desiredLocations.filterNot(
        (p: any) => p.isEmpty()
      );

      if (completeLocations.count() === 0) {
        error = 'Required';
      }
      break;
    }

    case 'currentLocation': {
      const { currentLocation } = talentProfile;
      if (!currentLocation) {
        error = 'Required';
      } else if (currentLocation.isEmpty() || !currentLocation.isValid()) {
        error = 'Please enter a valid ZIP code';
      }
      break;
    }

    case 'email': {
      error = validateEmail(talentProfile.email);
      break;
    }

    case 'fullName': {
      if (parseFullName(talentProfile.fullName).length < 2) {
        error = 'Please enter both a first and last name';
      }
      break;
    }

    case 'nurseSpecialties': {
      const { nurseSpecialties } = talentProfile;
      let nestedErrors: NurseSpecialtiesErrors = ImmutableMap();

      nurseSpecialties.forEach(
        (ye: unknown, nsdId: NurseSpecialtyDescriptionId) => {
          const yearsExperience = Number.parseInt(ye as string, 10);

          if (Number.isNaN(yearsExperience)) {
            nestedErrors = nestedErrors.set(
              nsdId,
              'Please enter a number of years.'
            );
          } else if (yearsExperience >= 100) {
            nestedErrors = nestedErrors.set(
              nsdId,
              'Number of years must be less than 100.'
            );
          } else if (yearsExperience < 0) {
            nestedErrors = nestedErrors.set(
              nsdId,
              "Number of years can't be negative."
            );
          }
        }
      );

      if (!nestedErrors.isEmpty()) {
        error = nestedErrors;
      }
      break;
    }

    case 'password': {
      if (talentProfile.password.length < 6) {
        error = 'Your password must be at least 6 characters.';
      }
      break;
    }

    case 'phone': {
      const parsedNumber = parsePhoneNumber(talentProfile.phone);
      if (!parsedNumber) {
        error =
          'Oops, this phone number appears to be invalid. If this ' +
          'is not a US number please make sure to start it with a + and ' +
          'your country code.';
      }
      break;
    }

    case 'resumeFormData': {
      const { workExperienceMethod, resumeFormData, resumeFilename } =
        talentProfile;

      if (
        workExperienceMethod === 'upload' &&
        !(resumeFormData || resumeFilename)
      ) {
        error = 'Please upload a resume file.';
      }
      break;
    }

    case 'workExperiences': {
      const { workExperienceMethod, workExperiences } = talentProfile;
      if (workExperienceMethod === 'manual_entry') {
        let { errors } = validateWorkExperiences(workExperiences) as {
          errors: WorkExperiencesErrors;
        };
        const workExperiencesEmpty =
          workExperiences.filterNot((we: any) => we.isEmpty()).size === 0;

        if (workExperiencesEmpty) {
          errors = errors.set(
            'base',
            'Please specify at least one work experience.'
          );
        }

        if (!errors.isEmpty()) {
          error = errors;
        }
      }
      break;
    }

    default:
      break;
  }

  return error;
}

/**
 * Validates the keys in the talent profile and returns an error object
 * with any error messages.
 */
function validateTalentProfile(
  talentProfile: any,
  keys: readonly string[],
  allRequiredKeys: readonly string[] = defaultAllRequiredKeys
): TalentProfileValidationErrors {
  const requiredKeys = keys.filter((key) => allRequiredKeys.includes(key));

  let errors = validateRequired(
    talentProfile,
    ...requiredKeys
  ) as TalentProfileValidationErrors;

  keys.forEach((key) => {
    if (!errors.get(key)) {
      const error = additionalErrorForKey(talentProfile, key);
      if (error) {
        errors = errors.set(key, error);
      }
    }
  });

  return errors;
}

export function validateTalentProfileField<K extends TalentProfileFieldName>(
  key: K,
  value: unknown
): TalentProfileFieldError<K> | undefined {
  const validationMap = validateTalentProfile({ [key]: value }, [key]);

  return validationMap.get(key) as TalentProfileFieldError<K> | undefined;
}

export default validateTalentProfile;
